import { useTranslation } from '@silospot/i18n/react';
import type { RiskIpRow, ThreatRow } from '@silospot/shared';
import { useQuery } from '@tanstack/react-query';
import { type ReactElement, type ReactNode, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Badge,
  Card,
  CardHead,
  type Column,
  DataTable,
  EstimatedNote,
  Hint,
  Kpi,
  KpiGrid,
  PageHeader,
  RiskPill,
  Segmented,
  Verdict,
  VerdictScale,
} from '../../components/cld';
import { AreaChart, BarsH, Donut, Gauge } from '../../components/cld/charts';
import { Flag } from '../../components/location';
import { api } from '../../lib/api';
import { fmtHM } from '../../lib/datetime';
import { usePeriodRange } from '../../stores/period-store';

const DEVICE_COLOR: Record<string, string> = {
  desktop: '#2563eb',
  mobile: '#0891b2',
  tablet: '#7c3aed',
};
function deviceColor(kind: string): string {
  return DEVICE_COLOR[kind.toLowerCase()] ?? '#98a0ae';
}

/** Integration-gated value (Google Ads, Palier C): never a fake number. */
function Locked({ hint }: { hint: string }): ReactElement {
  return (
    <span className="inline-flex items-center gap-1 font-mono text-text-3">
      —<Hint text={hint} />
    </span>
  );
}

export function OverviewPage(): ReactElement {
  const { t } = useTranslation('pages');
  const { t: ts } = useTranslation('shell');
  const { id = '' } = useParams();
  const range = usePeriodRange();
  const [style, setStyle] = useState<'cards' | 'dense' | 'focus'>('cards');

  const { data, isLoading } = useQuery({
    queryKey: ['cld-overview', id, range],
    queryFn: () => api.getOverview(id, range),
    enabled: !!id,
    // Auto-refresh so the first signal shows up on its own (no manual reload):
    // poll fast while still at zero visits, then ease off once data flows in.
    refetchInterval: (q) => ((q.state.data?.kpis?.visits ?? 0) === 0 ? 8000 : 60000),
    refetchOnWindowFocus: true,
  });

  const nf = new Intl.NumberFormat('fr-FR');
  const fmt = (n: number) => nf.format(n);
  const pct = (n: number) => `${nf.format(Math.round(n * 10) / 10)} %`;
  const adsHint = t('overview.fraudClicks'); // reused as the gated-metric tooltip label

  const header = (
    <PageHeader
      title={ts('nav.overview')}
      sub={t('overview.subtitle')}
      actions={
        <Segmented
          value={style}
          onChange={setStyle}
          options={[
            { value: 'cards', label: t('overview.styleCards') },
            { value: 'dense', label: t('overview.styleDense') },
            { value: 'focus', label: t('overview.styleFocus') },
          ]}
        />
      }
    />
  );

  if (isLoading || !data) {
    return (
      <div>
        {header}
        <Card>
          <div className="h-64 animate-pulse rounded-lg bg-neutral-bg" />
        </Card>
      </div>
    );
  }

  const k = data.kpis;
  const x = data.extras;
  const visits = k.visits || 1;
  const cleanShare = ((k.visits - k.suspect) / visits) * 100;
  const fraudShare = (k.suspect / visits) * 100;
  // Hour buckets (short periods) are labelled in Brussels time; day buckets keep the
  // date. (`t` is "YYYY-MM-DD HH:mm:ss" for hours, "YYYY-MM-DD" for days.)
  const labels = data.series.map((p) => (p.t.length > 10 ? fmtHM(p.t) : p.t.slice(5)));
  const cleanSeries = data.series.map((p) => p.visits - p.suspect);
  const fraudSeries = data.series.map((p) => p.suspect);

  const trafficChart = (
    <AreaChart
      labels={labels}
      series={[
        { name: t('overview.seriesClean'), color: '#2563eb', data: cleanSeries },
        { name: t('overview.seriesFraud'), color: '#dc2626', data: fraudSeries },
      ]}
    />
  );

  const threatFeed = (n: number): ReactNode => (
    <div className="flex flex-col">
      {x.recentThreats.slice(0, n).map((e: ThreatRow, i) => (
        <div
          key={e.visitId}
          className={`flex items-center gap-3 py-2.5 ${i < n - 1 ? 'border-b border-[#f1f3f6]' : ''}`}
        >
          <span className="h-2 w-2 shrink-0 rounded-full bg-fraud-fg" />
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-medium text-text">
              {t(`signal.${e.signal}`)}
            </div>
            <div className="truncate font-mono text-[11.5px] text-text-3">
              {e.ip} · <Flag country={e.country} /> {e.country} · {e.page || '/'}
            </div>
          </div>
          <Badge tone="suspect">{t('action.detected')}</Badge>
        </div>
      ))}
      {x.recentThreats.length === 0 ? (
        <p className="py-6 text-center text-[13px] text-text-3">—</p>
      ) : null}
    </div>
  );

  const ipColumns: Column<RiskIpRow>[] = [
    {
      key: 'ip',
      label: t('overview.colIp'),
      render: (r) => <span className="font-mono text-text">{r.ip}</span>,
    },
    {
      key: 'country',
      label: t('overview.colCountry'),
      render: (r) => (
        <span className="inline-flex items-center gap-1.5">
          <Flag country={r.country} /> {r.country || '—'}
        </span>
      ),
    },
    {
      key: 'connType',
      label: t('overview.colType'),
      sortable: false,
      render: (r) => (
        <Badge
          tone={r.proxyProbable ? 'info' : r.connType === 'datacenter' ? 'suspect' : 'neutral'}
        >
          {r.proxyProbable ? t('signal.proxy') : r.connType}
        </Badge>
      ),
    },
    {
      key: 'visits',
      label: t('overview.colVisits'),
      align: 'right',
      render: (r) => <span className="font-mono">{r.visits}</span>,
    },
    {
      key: 'score',
      label: t('overview.colRisk'),
      align: 'right',
      sortValue: (r) => r.score,
      render: (r) => <RiskPill value={r.score} />,
    },
    {
      key: 'status',
      label: t('overview.colStatus'),
      align: 'right',
      sortable: false,
      render: (r) => <Verdict score={r.score} showScore={false} size="sm" />,
    },
  ];

  return (
    <div>
      {header}

      {style === 'cards' && (
        <div className="flex flex-col gap-4">
          <KpiGrid>
            <Kpi label={t('overview.kpiVisits')} value={fmt(k.visits)} />
            <Kpi label={t('overview.kpiUniques')} value={fmt(x.uniques)} />
            <Kpi label={t('overview.kpiClean')} value={pct(cleanShare)} />
            <Kpi label={t('overview.kpiFraud')} value={pct(fraudShare)} />
            <Kpi label={t('signal.bot')} value={fmt(k.bots)} />
            <Kpi label={t('overview.kpiWasted')} value={<Locked hint={adsHint} />} />
          </KpiGrid>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
            <Card>
              <CardHead title={t('overview.trafficTitle')} sub={t('overview.trafficSub')} />
              {trafficChart}
            </Card>
            <Card>
              <CardHead title={t('overview.riskTitle')} hint={t('overview.riskTitle')} />
              <div className="flex justify-center py-1">
                <Gauge value={x.riskScore} />
              </div>
              <div className="mb-3">
                <VerdictScale score={x.riskScore} />
              </div>
              <div className="flex flex-col gap-2.5 text-[13px]">
                <Row label={t('overview.proxyVpn')}>
                  <Badge tone="info">{fmt(x.proxy)}</Badge>
                </Row>
                <Row label={t('overview.fraudClicks')}>
                  <Locked hint={adsHint} />
                </Row>
                <Row label={t('overview.recidivists')}>
                  <Badge tone="bot">{fmt(data.recidivists)}</Badge>
                </Row>
              </div>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card>
              <CardHead title={t('overview.devices')} />
              <div className="flex items-center gap-4">
                <Donut
                  data={x.devices.map((d) => ({
                    label: d.kind,
                    value: d.visits,
                    color: deviceColor(d.kind),
                  }))}
                />
                <div className="flex flex-1 flex-col gap-2">
                  {x.devices.map((d) => (
                    <div key={d.kind} className="flex items-center gap-2 text-[12.5px]">
                      <span
                        className="h-2.5 w-2.5 rounded-[3px]"
                        style={{ background: deviceColor(d.kind) }}
                      />
                      <span className="flex-1 text-[#3a4151] capitalize">{d.kind || '—'}</span>
                      <span className="font-mono text-text-2">{fmt(d.visits)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
            <Card>
              <CardHead title={t('overview.sources')} sub={t('overview.sourcesSub')} />
              <BarsH
                data={x.sourcesQuality.slice(0, 6).map((s) => ({
                  label: s.sourceType,
                  value: s.visits,
                  risk: s.visits > 0 ? (s.suspect / s.visits) * 100 : 0,
                }))}
                valueFmt={fmt}
              />
            </Card>
            <Card>
              <CardHead title={t('overview.recentThreats')} />
              {threatFeed(5)}
            </Card>
          </div>

          <EstimatedNote />
        </div>
      )}

      {style === 'dense' && (
        <div className="flex flex-col gap-3.5">
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-border bg-border sm:grid-cols-4 lg:grid-cols-7">
            {[
              [t('overview.colVisits'), fmt(k.visits), '#2563eb'],
              [t('overview.kpiClean'), pct(cleanShare), '#16a34a'],
              [t('overview.kpiFraud'), pct(fraudShare), '#dc2626'],
              [t('signal.bot'), fmt(k.bots), '#7c3aed'],
              [t('overview.dProxy'), fmt(x.proxy), '#0891b2'],
              [t('overview.dRecidive'), fmt(data.recidivists), '#db2777'],
              [t('overview.kpiUniques'), fmt(x.uniques), '#0f766e'],
            ].map((s) => (
              <div key={s[0]} className="bg-surface p-3">
                <div className="text-[11px] uppercase tracking-[0.03em] text-text-3">{s[0]}</div>
                <div className="mt-1 font-mono text-[18px] font-bold" style={{ color: s[2] }}>
                  {s[1]}
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-[1.6fr_1fr]">
            <Card>
              <CardHead title={t('overview.traffic30')} />
              {trafficChart}
            </Card>
            <Card>
              <CardHead
                title={t('overview.liveThreats')}
                right={
                  <Badge tone="fraud" dot>
                    live
                  </Badge>
                }
              />
              {threatFeed(7)}
            </Card>
          </div>
          <Card>
            <CardHead title={t('overview.topRiskIp')} sub={t('overview.topRiskSub')} />
            <DataTable
              columns={ipColumns}
              rows={x.topRiskIps}
              rowKey={(r) => r.ip}
              initialSort={{ key: 'score', dir: 'desc' }}
            />
          </Card>
        </div>
      )}

      {style === 'focus' && (
        <div className="flex flex-col gap-4">
          <Card className="bg-gradient-to-b from-white to-[#fbfcfe] p-7">
            <div className="grid grid-cols-1 items-center gap-7 md:grid-cols-[1.1fr_1fr]">
              <div>
                <Badge tone={fraudShare >= 10 ? 'suspect' : 'clean'} dot>
                  {t('overview.focusBadge')}
                </Badge>
                <div className="mt-3 font-mono text-[58px] font-bold leading-none tracking-[-0.03em] text-text">
                  {pct(fraudShare)}
                </div>
                <p className="mt-3 max-w-md text-[16px] leading-relaxed text-[#3a4151]">
                  {t('overview.kpiFraud')} —{' '}
                  <strong className="text-fraud-fg">{fmt(k.suspect)}</strong>{' '}
                  {t('action.flagged').toLowerCase()}.
                </p>
              </div>
              <div className="flex justify-center">
                <Gauge value={x.riskScore} size={240} />
              </div>
            </div>
          </Card>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Kpi label={t('overview.kpiVisits')} value={fmt(k.visits)} />
            <Kpi label={t('overview.kpiClean')} value={pct(cleanShare)} />
            <Kpi label={t('overview.validConversions')} value={fmt(k.conversions)} />
          </div>
          <Card>
            <CardHead title={t('overview.trafficEvolution')} sub={t('overview.trafficSub')} />
            {trafficChart}
          </Card>
          <EstimatedNote />
        </div>
      )}
    </div>
  );
}

function Row({ label, children }: { label: ReactNode; children: ReactNode }): ReactElement {
  return (
    <div className="flex items-center justify-between">
      <span className="text-text-2">{label}</span>
      {children}
    </div>
  );
}
